import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { setAdminUser, setAdminToken } from '../../utils/authSessionStorage';

/**
 * Super Admin Login component with glassmorphism design.
 * Features links to Privacy Policy and Terms & Conditions.
 */
const SuperAdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [legalModal, setLegalModal] = useState({ open: false, title: '', content: '' });
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login({ email, password });
      if (response.data.success) {
        const { token, user } = response.data.payload;
        
        // Role check for Super Admin
        if (!/super\s*admin/i.test(user.role || '')) {
          setError('Access denied. This portal is for Super Admins only.');
          setLoading(false);
          return;
        }

        setAdminToken(token);
        setAdminUser(user);
        navigate('/dashboard');

      } else {
        setError(response.data.message || 'Login failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const openLegalModal = (type) => {
    // In a real app, these would fetch from the LegalPages content
    const content = type === 'privacy' 
      ? 'Privacy Policy content would be displayed here...' 
      : 'Terms & Conditions content would be displayed here...';
    setLegalModal({ open: true, title: type === 'privacy' ? 'Privacy Policy' : 'Terms & Conditions', content });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at top left, #1e3c72 0%, #2a5298 100%)',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#fff',
            textAlign: 'center',
          }}
        >
          <Box sx={{ mb: 4 }}>
            <AdminIcon sx={{ fontSize: 64, color: '#fff', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: 1 }}>
              SUPER ADMIN
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.8 }}>
              Welivin Management Portal
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              placeholder="Admin Email"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#fff' },
                },
                input: { color: '#fff' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                mb: 4,
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#fff' },
                },
                input: { color: '#fff' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                background: '#fff',
                color: '#1e3c72',
                fontWeight: 700,
                fontSize: '1.1rem',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.9)',
                },
                mb: 3
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'LOGIN AS SUPER ADMIN'}
            </Button>
          </form>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, opacity: 0.7 }}>
            <Link
              component="button"
              onClick={() => openLegalModal('privacy')}
              sx={{ color: '#fff', fontSize: '0.85rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Privacy Policy
            </Link>
            <Link
              component="button"
              onClick={() => openLegalModal('terms')}
              sx={{ color: '#fff', fontSize: '0.85rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Terms & Conditions
            </Link>
          </Box>
        </Paper>
      </Container>

      <Dialog open={legalModal.open} onClose={() => setLegalModal({ ...legalModal, open: false })} maxWidth="md" fullWidth>
        <DialogTitle>{legalModal.title}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">{legalModal.content}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLegalModal({ ...legalModal, open: false })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminLogin;
